import type { ReactNode } from "react";
import styled from "styled-components";
import privacyPolicyContent from "./privacyPolicyContent.txt?raw";

const PURPLE = "#3a0ca3";
const ACCENT = "#6936dd";
const TEXT = "#303030";
const MUTED = "#707070";

const Document = styled.article<{ $embedded: boolean }>`
	box-sizing: border-box;
	width: 100%;
	padding: ${({ $embedded }) => ($embedded ? "32px" : "80px")};
	display: flex;
	flex-direction: column;
	gap: 48px;
	background: ${({ $embedded }) => ($embedded ? "transparent" : "#f3f5ff")};
	color: ${TEXT};
	font-family: Poppins, sans-serif;
	font-size: 14px;
	line-height: 24px;

	* {
		box-sizing: border-box;
	}

	@media (max-width: 700px) {
		padding: ${({ $embedded }) => ($embedded ? "20px 16px" : "32px 20px")};
		gap: 32px;
	}
`;

const Header = styled.header`
	padding-bottom: 24px;
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 24px;
`;

const Logo = styled.img`
	display: block;
	width: 240px;
	height: auto;
	max-width: 65%;
`;

const Eyebrow = styled.span`
	color: ${ACCENT};
	font-size: 12px;
	font-weight: 500;
	white-space: nowrap;
`;

const TitleBlock = styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;
`;

const Title = styled.h1`
	margin: 0;
	font-size: 38px;
	line-height: 1.25;
	font-weight: 700;

	@media (max-width: 700px) {
		font-size: 28px;
	}
`;

const Subtitle = styled.p`
	margin: 0;
`;

const Metadata = styled.div`
	display: flex;
	align-items: center;
	gap: 24px;
	flex-wrap: wrap;
`;

const Version = styled.span`
	padding: 4px 12px;
	border-radius: 999px;
	background: ${ACCENT};
	color: #f3f5ff;
	font-size: 12px;
	line-height: normal;
	font-weight: 500;
`;

const DateMeta = styled.span`
	display: inline-flex;
	gap: 16px;
	white-space: nowrap;

	span {
		color: ${MUTED};
	}
`;

const Content = styled.div`
	display: flex;
	flex-direction: column;
	gap: 40px;
`;

const FooterPreview = styled.footer`
	padding-top: 40px;
	color: #e8590d;
	text-align: center;
`;

const Section = styled.section`
	display: flex;
	flex-direction: column;
	gap: 12px;
`;

const SectionHeader = styled.div`
	display: grid;
	grid-template-columns: 40px minmax(0, 1fr);
	gap: 32px;
	color: ${PURPLE};
	font-weight: 600;
`;

const SectionTitle = styled.h2`
	margin: 0;
	font: inherit;
`;

const SectionBody = styled.div`
	padding-left: 72px;
	display: flex;
	flex-direction: column;
	gap: 12px;

	@media (max-width: 700px) {
		padding-left: 0;
	}
`;

const Paragraph = styled.p`
	margin: 0;
`;

const Subsection = styled.h3`
	margin: 4px 0 0;
	font-size: 14px;
	line-height: 24px;
	font-weight: 600;
`;

const PurpleSubsection = styled(Subsection)`
	color: ${PURPLE};
`;

const Bullet = styled.p`
	margin: 0;
	padding-left: 16px;
`;

const Unresolved = styled.span`
	color: #e8590d;
`;

const TableWrap = styled.div`
	width: 100%;
	overflow-x: auto;
	border: 1px solid #cdd4f6;
	border-radius: 8px;
`;

const Table = styled.table`
	width: 100%;
	min-width: 850px;
	border-collapse: collapse;
	background: #ebeefd;

	th,
	td {
		padding: 12px 16px;
		border-bottom: 1px solid #cdd4f6;
		text-align: left;
		vertical-align: top;
	}

	th {
		background: #dde2fa;
		color: #011992;
		font-weight: 600;
	}

	th:first-child,
	td:first-child {
		width: 256px;
		font-weight: 600;
	}

	tr:last-child td {
		border-bottom: 0;
	}
`;

const RichText = ({ children }: { children: string }) => {
	const placeholderPattern = /(\[to be confirmed\]|\[key processors to be listed\])/g;
	const parts = children.split(placeholderPattern);

	return (
		<>
			{parts.map((part, index) =>
				part === "[to be confirmed]" || part === "[key processors to be listed]" ? (
					<Unresolved key={`${part}-${index}`}>{part}</Unresolved>
				) : (
					<RichSegment key={`${part}-${index}`}>{part}</RichSegment>
				),
			)}
		</>
	);
};

const RichSegment = ({ children }: { children: string }) => {
	const colon = children.match(/^([^:]{2,70}:)(.*)$/);
	if (colon) {
		return (
			<>
				<strong>{colon[1]}</strong>
				{colon[2]}
			</>
		);
	}
	return children;
};

const lines = privacyPolicyContent
	.split(/\r?\n/)
	.map((line) => line.trim())
	.filter(Boolean);

const metadata = lines[3]?.match(/^Effective date:\s*(.*?)\s*·\s*Last updated:\s*(.*)$/);
const effectiveDate = metadata?.[1] ?? "";
const lastUpdated = metadata?.[2] ?? "";

export const PRIVACY_POLICY_META = {
	title: "Privacy Policy",
	version: "Version 1.1",
	companyName: "CREATORVERSE PLATFORM AND CREATOR PAYMENTS",
	effectiveDate,
	lastUpdated,
} as const;

const sections = lines.slice(4).reduce<Array<{ number: string; title: string; lines: string[] }>>((result, line) => {
	const match = line.match(/^(\d+)\.\s+(.*)$/);
	if (match && !/^\d+\.\d+/.test(line)) {
		result.push({ number: match[1], title: match[2], lines: [] });
	} else {
		result.at(-1)?.lines.push(line);
	}
	return result;
}, []);

const companyRows = [
	["UEN", "202629528Z", "202629513M"],
	["Registered office", "69 Circular Road, #02-01, Singapore 049423", "69 Circular Road, #02-01, Singapore 049423"],
	[
		"What it does",
		"The Creatorverse platform — your profile, campaign workflow, creator discovery, reporting and analytics",
		"The payment service — preparing your Payment Statements and paying you",
	],
	[
		"What it collects about you",
		"Identity and contact details, profile and social data, campaign and engagement data, device and usage data",
		"Your legal name and entity type, address, country of residence, tax registration status and number, bank details, payment history",
	],
];

const retentionRows = [
	[
		"Financial and tax records — your Payment Statements, the amounts and dates paid, project codes, and the tax declarations behind them",
		"Creator Payments",
		"7 years from the end of the financial year they relate to, or longer where the law of your country requires it",
		"Singapore’s statutory minimum is 5 years; 7 gives us audit headroom and covers markets with longer periods",
	],
	[
		"Records of your acceptance of our terms and of each Payment Statement",
		"Both",
		"7 years",
		"These have to outlive the payments they evidence",
	],
	[
		"Your bank account details",
		"Creator Payments",
		"Only while your payments engagement is active, and 12 months afterwards",
		"See the note below — this is deliberately much shorter than the 7 years above",
	],
	[
		"Your account and profile data",
		"Creatorverse",
		"Life of your account, and 12 months afterwards",
		"So you can come back without redoing onboarding",
	],
	[
		"Talent Data from your linked social accounts",
		"Creatorverse",
		"Life of your account, and 12 months afterwards",
		"Same",
	],
	[
		"Device, IP address and usage data",
		"Creatorverse",
		"24 months",
		"Its usefulness for analytics and security falls away quickly",
	],
	[
		"Support messages and correspondence",
		"Both",
		"3 years",
		"Long enough to deal with a dispute about a campaign or a payment",
	],
	[
		"Sign-ups that were never completed",
		"Creatorverse",
		"90 days",
		"We never traded with you, so there is nothing to keep",
	],
];

const renderStandardLine = (line: string, sectionNumber: string): ReactNode => {
	if (line.startsWith("•")) {
		return (
			<Bullet key={line}>
				• <RichText>{line.replace(/^•\s*/, "")}</RichText>
			</Bullet>
		);
	}
	if (/^\d+\.\d+\s/.test(line)) {
		return (
			<Subsection key={line}>
				<RichText>{line}</RichText>
			</Subsection>
		);
	}
	if (
		[
			"About your tax data specifically",
			"Legal bases (EU/UK GDPR, where it applies)",
			"Consent and notification (PDPA)",
			"Why your bank details are kept for less time than your payment records",
		].includes(line)
	) {
		return <Subsection key={line}>{line}</Subsection>;
	}
	if (sectionNumber === "4" && line.endsWith("uses your data to:")) {
		return <PurpleSubsection key={line}>{line}</PurpleSubsection>;
	}
	return (
		<Paragraph key={line}>
			<RichText>{line}</RichText>
		</Paragraph>
	);
};

const PolicySection = ({ number, title, bodyLines }: { number: string; title: string; bodyLines: string[] }) => {
	let body: ReactNode;

	if (number === "2") {
		body = (
			<>
				<Paragraph>{bodyLines[0]}</Paragraph>
				<TableWrap>
					<Table>
						<thead>
							<tr>
								<th aria-label="Field" />
								<th>CREATORVERSE PTE. LTD.</th>
								<th>CREATOR PAYMENTS INTERNATIONAL PTE. LTD.</th>
							</tr>
						</thead>
						<tbody>
							{companyRows.map(([field, creatorverse, payments]) => (
								<tr key={field}>
									<td>{field}</td>
									<td>{creatorverse}</td>
									<td>{payments}</td>
								</tr>
							))}
						</tbody>
					</Table>
				</TableWrap>
				{bodyLines.slice(15).map((line) => renderStandardLine(line, number))}
			</>
		);
	} else if (number === "7") {
		body = (
			<>
				<Paragraph>{bodyLines[0]}</Paragraph>
				<TableWrap>
					<Table>
						<thead>
							<tr>
								<th>What</th>
								<th>Held by</th>
								<th>How long</th>
								<th>Why</th>
							</tr>
						</thead>
						<tbody>
							{retentionRows.map(([what, heldBy, howLong, why]) => (
								<tr key={what}>
									<td>{what}</td>
									<td>{heldBy}</td>
									<td>{howLong}</td>
									<td>{why}</td>
								</tr>
							))}
						</tbody>
					</Table>
				</TableWrap>
				{bodyLines.slice(37).map((line) => renderStandardLine(line, number))}
			</>
		);
	} else {
		body = bodyLines.map((line) => renderStandardLine(line, number));
	}

	return (
		<Section>
			<SectionHeader>
				<span>{number}.</span>
				<SectionTitle>{title}</SectionTitle>
			</SectionHeader>
			<SectionBody>{body}</SectionBody>
		</Section>
	);
};

/** `embedded`: rendered inside `LegalDocumentModal`, which owns the title block. */
export const PrivacyPolicyDocument = ({ embedded = false }: { embedded?: boolean }) => (
	<Document $embedded={embedded}>
		<Header>
			{embedded ? (
				<Logo src="/Creatorverse_Wordmark_Purple.svg" alt="Creatorverse" />
			) : (
				<>
					<Logo src="/Creatorverse_Wordmark_Gradient.svg" alt="Creatorverse" />
					<Eyebrow>LEGAL DOCUMENT</Eyebrow>
				</>
			)}
		</Header>
		{!embedded && (
			<TitleBlock>
				<Title>Privacy Policy</Title>
				<Subtitle>Creatorverse platform and Creator Payments</Subtitle>
				<Metadata>
					<Version>Version 1.1</Version>
					<DateMeta>
						<strong>Effective date:</strong>
						<span>{effectiveDate}</span>
					</DateMeta>
					<DateMeta>
						<strong>Last updated:</strong>
						<span>{lastUpdated}</span>
					</DateMeta>
				</Metadata>
			</TitleBlock>
		)}
		<Content>
			{sections.map((section) => (
				<PolicySection
					key={section.number}
					number={section.number}
					title={section.title}
					bodyLines={section.lines}
				/>
			))}
		</Content>
		<FooterPreview>
			© 2026 Creatorverse Platform. All corporate marks are properties of their respective owners.
		</FooterPreview>
	</Document>
);
